import { computed, ref } from 'vue';
import type { RouteLocationNormalizedLoaded, RouteMeta } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useBotsStore } from '@/stores/bots';
import { stageTypes } from '@/stores/welcome';

/**
 * The views a lead can open: the users of a bot in the `lead` stage, which the
 * demo creation funnel creates. Every other view shows the demo request instead.
 * Public routes (e.g. the bot preview) are always open.
 *
 * Deliberately client-side only: this keeps the lead from being overwhelmed and
 * steers them towards a demo. It is no access control.
 */
export const leadRouteNames: readonly string[] = [
  'ai',
  // The widget settings, and the page they're reached through. The teasers stay locked.
  'wt',
  'widget',
  'settings-roles',
  'user-profile',
  // The knowledge base list only. Editing a document stays locked.
  'knowledge-base',
  'knowledge-base-container',
];

/**
 * What the demo request says the lead tried to open: the sidebar's title of the
 * area, by the start of the locked route's name (e.g. `settings-privacy`).
 */
const lockedAreaTitleKeys: [routeNamePrefix: string, translationKey: string][] = [
  ['welcome', 'menu.dashboard'],
  ['livechat-statistics', 'settingsLivechat.livechatStats.title'],
  ['statistics', 'stats.title'],
  ['statsreport', 'stats.title'],
  ['livechat', 'livechat.title'],
  ['chat', 'menu.conversations'],
  ['conversations', 'menu.conversations'],
  ['insights', 'menu.conversations'],
  ['intent', 'menu.answers'],
  ['conversational', 'menu.conversational'],
  ['form', 'menu.forms'],
  ['teaser', 'widgetTeaser.teaser.title'],
  ['knowledge-base', 'menu.knowledgeBase'],
  ['settings', 'menu.settings'],
  ['integrations', 'menu.integrations'],
];

/** @returns The translation key of the locked area's title, `null` if there's none to name */
export const lockedAreaTitleKey = (route: RouteLocationNormalizedLoaded): string | null => {
  const name = String(route.name ?? '').toLowerCase();
  return lockedAreaTitleKeys.find(([prefix]) => name.startsWith(prefix))?.[1] ?? null;
};

/**
 * The demo request opened from inside a view the lead can open, e.g. by an "add"
 * button in the knowledge base. Shared, as the triggers sit deep in the views
 * while the overlay is rendered by `GenericWrapper`.
 */
const demoRequestOpen = ref(false);

export const useLeadRestriction = () => {
  const authStore = useAuthStore();
  const botsStore = useBotsStore();

  /**
   * Whether the current user sees the reduced Hub. moinAI admins never do.
   * Lifts by itself once the bot moves on from the `lead` stage.
   */
  const isRestricted = computed(() => botsStore.currentBotStage === stageTypes.LEAD && !authStore.isAdmin);

  /** Whether a route shows the demo request instead of its view. Also takes a resolved route, e.g. of a link. */
  const isLocked = (route: { name?: unknown; meta?: RouteMeta }): boolean => isRestricted.value
    && !route.meta?.public
    && !leadRouteNames.includes(String(route.name));

  const openDemoRequest = () => {
    demoRequestOpen.value = true;
  };
  const closeDemoRequest = () => {
    demoRequestOpen.value = false;
  };

  return { isRestricted, isLocked, demoRequestOpen, openDemoRequest, closeDemoRequest };
};
